import type { QueueItem } from './types'

// How many recent tracks the DJ recaps and how many upcoming it previews.
const PAST_COUNT = 5
const NEXT_COUNT = 3

// Tracks after the current one that play before the break airs. A wider gap
// gives slow (local) synthesis more time to finish before the playhead reaches
// the slot, so fewer breaks are dropped as stale. Kept within the radio buffer's
// guaranteed depth (RADIO_THRESHOLD) so the anchor track reliably exists.
const LOOKAHEAD_TRACKS = 2

export interface InterstitialRequest {
  /** Track the break airs immediately after; anchors both context and placement. */
  anchorId: number
  /** Recent tracks up to and including the anchor (play order). */
  pastIds: number[]
  /** Tracks that play after the anchor (i.e. after the break airs). */
  nextIds: number[]
}

/**
 * Build the interstitial request for the slot after `currentIndex`, or null when a
 * break can't be placed there. The break airs immediately after the anchor track
 * (`queue[currentIndex + LOOKAHEAD_TRACKS]`) — the lookahead gap that lets synthesis
 * finish before the slot is reached. Both the script context and the eventual
 * insertion point derive from the anchor so they can't drift apart.
 */
export function planInterstitial(
  queue: QueueItem[],
  currentIndex: number
): InterstitialRequest | null {
  const anchorIndex = currentIndex + LOOKAHEAD_TRACKS
  const anchor = queue[anchorIndex]
  if (!anchor || anchor.isInterstitial) return null
  // A break already sits anywhere in the lookahead window (up to the slot right
  // after the anchor) — don't stack another.
  for (let i = currentIndex + 1; i <= anchorIndex + 1; i++) {
    if (queue[i]?.isInterstitial) return null
  }

  const pastIds = queue
    .slice(0, anchorIndex + 1)
    .filter((t) => !t.isInterstitial)
    .slice(-PAST_COUNT)
    .map((t) => t.id)

  const nextIds = queue
    .slice(anchorIndex + 1)
    .filter((t) => !t.isInterstitial)
    .slice(0, NEXT_COUNT)
    .map((t) => t.id)

  if (pastIds.length === 0 && nextIds.length === 0) return null

  return { anchorId: anchor.id, pastIds, nextIds }
}

/**
 * Live queue index at which to insert a synthesized break, or null to drop it.
 * Returns null when the anchor is gone or the playhead already passed it
 * (synthesis outran the lookahead), so a recap/preview that no longer matches
 * never airs.
 */
export function resolveInterstitialSlot(
  queue: QueueItem[],
  currentIndex: number,
  anchorId: number
): number | null {
  const anchorIdx = queue.findIndex((t) => t.id === anchorId)
  if (anchorIdx === -1 || anchorIdx < currentIndex) return null
  return anchorIdx + 1
}
